import { useEffect } from 'react';
import { useLocation } from 'react-router';

const STORAGE_KEY = 'routeQueryMemory';

type Memory = Record<string, string>;

const read = (): Memory => {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? '{}');
  } catch {
    return {};
  }
};

const write = (memory: Memory) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(memory));
  } catch {
    // sessionStorage may be unavailable (private mode etc.); recall then just
    // returns '' and "up" navigation falls back to the bare parent path.
  }
};

/** Records the query string last seen at a given pathname. */
export const rememberRouteQuery = (pathname: string, search: string) => {
  const memory = read();
  if (search) {
    if (memory[pathname] === search) {
      return;
    }
    memory[pathname] = search;
  } else if (pathname in memory) {
    delete memory[pathname];
  } else {
    return;
  }
  write(memory);
};

/** Returns the last query string seen at a pathname, or '' if none is known. */
export const recallRouteQuery = (pathname: string): string =>
  read()[pathname] ?? '';

/**
 * Records the query string of every visited route so that "up" navigation can
 * restore a parent list exactly as the user left it (filters, selection),
 * surviving reloads via sessionStorage. Mount once near the app root.
 */
export const useRecordRouteQuery = () => {
  const { pathname, search } = useLocation();
  useEffect(() => {
    rememberRouteQuery(pathname, search);
  }, [pathname, search]);
};
